import {Component, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {ApiClientService} from '../../../../services/api-client/api-client.service';
import {TranslationDomainService} from '../../../../services/translation-domain/translation-domain.service';
import {TranslationDomain} from '../../../../models/translation/translation-domain';
import {LoaderService} from '../../../../services/loader/loader.service';
import {SnackbarService} from '../../../../services/snackbar/snackbar.service';
import {FormsModule} from '@angular/forms';
import {InputSwitchComponent} from '../../../layout/input-switch/input-switch.component';

import {MetaTitle} from '../../../../decorator/meta-title';
import {DialogService} from '../../../../services/dialog/dialog.service';
import {TranslationImportComponent} from './translation-import/translation-import.component';
import {Translation} from '../../../../types/translation';
import {
  TranslationKnowledgeComponent
} from '../translation-message-list/translation-knowledge/translation-knowledge.component';

@Component({
  selector: 'app-translation-integration',
  imports: [
    FormsModule,
    InputSwitchComponent
],
  templateUrl: './translation-integration.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './translation-integration.component.scss'
})
@MetaTitle('Translation integration')
export class TranslationIntegrationComponent implements OnInit {

  domains: TranslationDomain[] = [];
  searchDomain: string = '';
  domainId: number | null = null;
  force: boolean = false;
  cleanUpAfterSubmit: boolean = true;
  data: Translation[] = [
    {
      key: '',
      text: ''
    }
  ];

  submitting: boolean = false;

  constructor(
    private loader: LoaderService,
    private snackbar: SnackbarService,
    private apiClient: ApiClientService,
    private dialog: DialogService,
    private translationDomainService: TranslationDomainService
  ) {
  }

  ngOnInit(): void {
    this.loader.startLoading();
    this.translationDomainService.findAll().subscribe((domains: TranslationDomain[]) => {
      this.domains = domains;

      this.loader.stopLoading();
    });
  }

  openKnowledgeModal(): void {
    this.dialog.openDialog(TranslationKnowledgeComponent).subscribe(() => {
    });
  }

  openImportModal(): void {
    this.dialog.openDialog(TranslationImportComponent, {
      backdrop: false
    }).subscribe((data) => {
      this.removeLastEmptyEntry();

      if (data && Array.isArray(data)) {
        for (const value of data) {
          this.data.push({
            key: value.key,
            text: value.text
          })
        }
        this.data.push({key: '', text: ''});
      }
    });
  }

  filterSearch(domain: TranslationDomain): boolean {
    if (!this.searchDomain) {
      return true;
    }

    return domain.description.toLowerCase().includes(this.searchDomain.toLowerCase());
  }

  isEmptyData(): boolean {
    if (!this.data.length) {
      return true;
    }

    return this.data[0].key === '' || this.data[0].text === '';
  }

  duplicateIncrement(index: number): void {
    const data = this.data[index];
    if (!data || data.key === '') {
      return;
    }

    const inc = Number(prompt('Increment up to'));
    if (!isNaN(inc) && inc > 0) {
      const match = data.key.match(/^(.*?)(\d+)?$/);
      if (!match) return;

      let baseKey = match[1];
      let startInc = match[2] !== undefined ? parseInt(match[2], 10) : -1;

      if (startInc === -1) {
        startInc = 1;
        this.data[index].key = `${baseKey}${startInc}`;
      }

      const newItems = [];
      for (let i = startInc + 1; i <= inc; ++i) {
        newItems.push({
          key: `${baseKey}${i}`,
          text: data.text,
        });
      }

      this.data.splice(index + 1, 0, ...newItems);

      this.addElement();
    }
  }

  generateZodiac(): void {
    const message = prompt('Enter the key with %zodiac% for replacement during generation');
    if (message) {
      this.removeLastEmptyEntry();

      for (const zodiac of this.getZodiac()) {
        this.data.push({
          key: message.replace('%zodiac%', zodiac),
          text: ''
        })
      }

      this.data.push({key: '', text: ''});
    }

    this.addElement();
  }

  addElement(force: boolean = false): void {
    const lastElement = this.data[this.data.length - 1];
    if (force || (lastElement.key !== '' || lastElement.text !== '')) {
      this.data.push({key: '', text: ''});
    }
  }

  removeElement(index: number): void {
    const data = this.data[index];
    if (!data) {
      return;
    }

    if (index === 0) {
      this.data[0].key = '';
      this.data[0].text = '';
    } else {
      this.data.splice(index, 1);
    }
  }

  onSubmit(): void {
    if (!this.domainId) {
      this.snackbar.error('You must select a domain.');
      return;
    }

    if (this.isEmptyData()) {
      this.snackbar.error('Nothing to submit.');
      return;
    }

    const data = [];
    for (const translation of this.data) {
      if (translation.key !== '' && translation.text !== '') {
        translation.key = translation.key.toLowerCase();
        data.push(translation);
      }
    }

    if (!data.length) {
      this.snackbar.error('Nothing to submit.');
      return;
    }

    this.submitting = true;

    this.apiClient.post('/translation-integration', {}, {
      domainId: this.domainId,
      force: this.force,
      data,
    }).subscribe(() => {
      if (this.cleanUpAfterSubmit) {
        this.data = [
          {
            key: '',
            text: ''
          }
        ];
      }
      this.submitting = false;
      this.snackbar.success('Saved');
    });
  }

  private removeLastEmptyEntry(): void {
    const lastIndex = this.data.length - 1;
    if (this.data[lastIndex].key === '' && this.data[lastIndex].text === '') {
      this.data.pop();
    }
  }

  private getZodiac(): string[] {
    return ['aquarius', 'aries', 'cancer', 'capricorn', 'taurus', 'leo', 'gemini', 'pisces', 'sagittarius', 'scorpio', 'virgo', 'libra'];
  }
}
